import Box from '@app/components/common/Box';
import {
  EDateTypes,
  TTimePeriodTypes,
} from '@app/components/LoggedIn/Home/DashboardCreator/types';
import { SIDEBAR_WIDTH } from '@app/components/LoggedIn/SideBar/styles';
import { IWidget } from '@app/state/home/types';
import { removeCustomReportElements } from '@app/state/reports/actions';
import { useAppDispatch } from '@app/state/store';
import { scrollBarStyles } from '@app/theme/commonStyles';
import Widget from '@app/views/LoggedIn/Home/Widget';
import { useTheme } from '@emotion/react';
import {
  ICustomReport,
  ICustomReportComponent,
  IDashboardLayoutElement,
  reportsSelectors,
} from '@westondev/tableturn-core';
import { max } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ReactZoomPanPinchContentRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch';
import CustomReportsGridStack from '../CustomReportsGridStack';
import CustomReportEmpty from './CustomReportEmpty';
import CustomReportsTools from './CustomReportsTools';
import Page from './Page';
import {
  CUSTOM_REPORT_NUMBER_OF_ROWS,
  CUSTOM_REPORT_ROW_SIZE,
  CUSTOM_REPORT_WIDTH,
} from './types';

const pageSize = CUSTOM_REPORT_NUMBER_OF_ROWS * CUSTOM_REPORT_ROW_SIZE;

const CustomReportsCreator = () => {
  // Redux
  const dispatch = useAppDispatch();
  const currentEditCustomReport = useSelector(
    reportsSelectors.selectReportChangeData,
  ) as ICustomReport | null;
  const currentCustomReport = useSelector(
    reportsSelectors.selectReportOriginalData,
  ) as ICustomReport | null;

  const containerRef = useRef<HTMLDivElement>(null);
  const customReportContainer = useRef<ReactZoomPanPinchContentRef>(null);
  const theme = useTheme();
  const enableEdit = true;
  const isPreviewMode = false;
  const [showGridLines, setShowGridLines] = useState(false);
  const [numberOfPages, setNumberOfPages] = useState(1);
  const [scale, setScale] = useState(0.9);
  const [wrapperSize, setWrapperSize] = useState('100%');

  const filters = useMemo(() => {
    if (!currentEditCustomReport) {
      return {
        timePeriod: EDateTypes.yesterday as TTimePeriodTypes,
        startDate: '',
        endDate: '',
      };
    }

    return {
      timePeriod: currentEditCustomReport.period as TTimePeriodTypes,
      startDate: currentEditCustomReport.startDate,
      endDate: currentEditCustomReport.endDate,
    };
  }, [currentEditCustomReport]);

  const elements = useMemo(() => {
    const returnWidget = (
      widgets: Record<string, ICustomReportComponent | null>,
      element: IDashboardLayoutElement,
    ) => {
      const widgetId = element.widgetId;
      const widget = widgets[widgetId];
      if (!widget) return <></>;

      return (
        <Widget
          key={widgetId}
          widgetId={widgetId}
          filters={filters}
          widgetType={widget.type as IWidget['type']}
          title={widget.title}
          parameters={widget.parameters}
          alwaysShowExampleData={true}
        />
      );
    };

    if (enableEdit && !isPreviewMode && currentEditCustomReport) {
      return Object.values(currentEditCustomReport.layout).map(element => ({
        ...element,
        id: element.id.toString(),
        content: returnWidget(
          currentEditCustomReport.customReportComponents,
          element,
        ) as any,
      }));
    } else if (!enableEdit && currentCustomReport) {
      return Object.values(currentCustomReport.layout).map(element => ({
        ...element,
        id: element.id.toString(),
        content: returnWidget(
          currentCustomReport.customReportComponents,
          element,
        ) as any,
      }));
    }
    return [];
  }, [
    currentCustomReport,
    currentEditCustomReport,
    enableEdit,
    isPreviewMode,
    filters,
  ]);

  useEffect(() => {
    const getWrapperSize = () => {
      const wrapperHeight =
        customReportContainer.current?.instance.wrapperComponent
          ?.clientHeight ?? 0;
      if (window.innerHeight < 720) {
        if (scale === 0.9) {
          return `${wrapperHeight}px`;
        } else if (scale < 0.9 && scale > 0.8) {
          return `${wrapperHeight + 60}px`;
        } else if (scale < 0.8 && scale > 0.7) {
          return `${wrapperHeight + 80}px`;
        } else if (scale === 0.7) {
          return `${wrapperHeight + 100}px`;
        } else return `${wrapperHeight}px`;
      } else {
        if (scale === 0.9) {
          return `${wrapperHeight}px`;
        } else if (scale < 0.9 && scale > 0.8) {
          return `${wrapperHeight + 100}px`;
        } else if (scale < 0.8 && scale > 0.7) {
          return `${wrapperHeight + 140}px`;
        } else if (scale === 0.7) {
          return `${wrapperHeight + 190}px`;
        } else return `${wrapperHeight}px`;
      }
    };

    const adjustTransform = () => {
      if (window.innerWidth > CUSTOM_REPORT_WIDTH + parseInt(SIDEBAR_WIDTH)) {
        customReportContainer.current?.centerView();
      } else {
        customReportContainer.current?.setTransform(0, 0, 1);
      }
    };

    const handleResize = () => {
      setWrapperSize(getWrapperSize());
      adjustTransform();
      // handleCenterView();
    };
    setWrapperSize(getWrapperSize());

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [scale]);

  useEffect(() => {
    const adjustTransform = () => {
      if (window.innerWidth > CUSTOM_REPORT_WIDTH + parseInt(SIDEBAR_WIDTH)) {
        customReportContainer.current?.centerView();
      } else {
        customReportContainer.current?.setTransform(0, 0, 1);
      }
    };
    adjustTransform();
  }, []);

  useEffect(() => {
    if (elements.length === 0) return setNumberOfPages(1);
    const pages = elements.reduce((acc, element) => {
      const page =
        element.y + element.h > CUSTOM_REPORT_NUMBER_OF_ROWS
          ? Math.ceil((element.y + element.h) / CUSTOM_REPORT_NUMBER_OF_ROWS)
          : 1;
      if (!acc[page]) {
        acc.push(page);
      }
      return acc;
    }, [] as number[]);

    const numberOfPagesCount = max(pages) || 1;

    // If you  add a new page update the number of pages, otherwise do nothing
    if (numberOfPagesCount > numberOfPages)
      setNumberOfPages(numberOfPagesCount);
  }, [elements, numberOfPages]);

  const handleRemovePage = (page: number) => {
    if (page === 1 && numberOfPages === 1) {
      return dispatch(removeCustomReportElements());
    } else {
      dispatch(removeCustomReportElements(page));
      setNumberOfPages(prev => prev - 1);
    }
  };

  return (
    <Box
      csx={[
        enableEdit
          ? { overflow: 'hidden', height: 'calc(100% - 45px)' }
          : {
              overflow: 'unset',
              height: elements.length === 0 ? '100%' : 'initial',
            },
        {
          width: '100%',
          padding: '0px',
        },
      ]}>
      <TransformWrapper
        ref={customReportContainer}
        centerOnInit
        minScale={0.7}
        maxScale={1}
        initialScale={1}
        doubleClick={{ disabled: true }}
        customTransform={(x, _, _scale) => {
          setScale(_scale);
          return `translate(${x}px, ${0}px) scale(${_scale})`;
        }}
        pinch={{
          disabled: true,
        }}
        wheel={{
          disabled: true,
        }}
        panning={{
          disabled: true,
        }}>
        <CustomReportsTools
          showGridLines={showGridLines}
          setShowGridLines={setShowGridLines}
          onAddPage={() => {
            if (elements.length > 0) setNumberOfPages(prev => prev + 1);
          }}
        />
        <TransformComponent
          contentProps={{
            id: 'layoutArea',
          }}
          wrapperStyle={{
            width: '100%',
            height: `calc(100% - ${CUSTOM_REPORT_ROW_SIZE}px)`,
            overflowX: 'auto',
          }}
          contentStyle={{
            width: `${CUSTOM_REPORT_WIDTH}px`,
            height: wrapperSize,
            position: 'absolute',
          }}>
          <Box
            ref={containerRef}
            id="customReportCreatorContainer"
            csx={[
              {
                width: '100%',
                height: wrapperSize,
                position: 'relative',
                borderRadius: '8px',
                overflowY: enableEdit ? 'scroll' : 'hidden',
                overflowX: 'hidden',
                paddingInline: '3px',
                '.pageOptions': {
                  display: 'none',
                },
                ':hover': {
                  '.pageOptions': {
                    display: elements.length > 0 ? 'block' : 'none',
                  },
                },
              },
              scrollBarStyles(theme, true),
            ]}>
            {enableEdit &&
              numberOfPages > 0 &&
              Array.from({ length: numberOfPages }).map((_, index) => (
                <Page
                  key={`layoutGrid-${index}`}
                  index={index}
                  showGridLines={showGridLines}
                  onRemovePage={handleRemovePage}
                  pageSize={pageSize}
                />
              ))}
            <CustomReportsGridStack elements={elements} />
            <CustomReportEmpty
              elements={elements}
              enableEdit={enableEdit}
              isLoading={false}
              pageSize={pageSize}
            />
          </Box>
        </TransformComponent>
      </TransformWrapper>
    </Box>
  );
};

export default CustomReportsCreator;
